import React, {useEffect, useCallback, useState} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Link, useLocation } from 'react-router-dom';
import classnames from 'classnames';

import Container from '../Container/Container';
import Spinner from '../Spinner/Spinner';
import ToggleSwitch from '../Toggles/ToggleApproval';
import Paginate from '../Paginate/Paginate';
import {deleteUser, getUsers} from '../../redux/thunks/usersThunks';
import { isGranted, roles } from '../../helpers/roles';
import ToggleAgency from '../Toggles/ToggleAgency';

const UsersList = () => {
  const {
    data: users,
    pagination,
    isLoading,
  } = useSelector(state => state.users);
  const authUser = useSelector(state => state.auth.user);
  const dispatch = useDispatch();
  const isLoadingNextPage = isLoading && users.length;
  const location = useLocation();
  const [search, setSearch] = useState('');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  const isUrlContains = useCallback(
    str => location.pathname.includes(str),
    [location.pathname]
  );

  useEffect(() => {
    if (isUrlContains('office')) {
      dispatch(
        getUsers({ page: 1, 'office.location': authUser.office.location,itemsPerPage: 100 })
      );
    } else {
      dispatch(getUsers({ page: 1 ,itemsPerPage: 100}));
    }
  }, [dispatch, isUrlContains, authUser.office.location]);

  useEffect(() => {
    dispatch(getUsers({ page: 1, 'fullname': search, itemsPerPage: 100 }));
  }, [dispatch, search]);

  if (isLoading && !users.length) {
    return (
      <Container>
        <Spinner loading />
      </Container>
    );
  }

  if (!users.length && search === '') {
    return <Container />;
  }

  const handlePageClick = event => {
    if (isUrlContains('office')) {
      dispatch(
        getUsers({
          page: event.selected + 1,
          'office.location': authUser.office.location,
          itemsPerPage: 100 
        })
      );
    } else {
      dispatch(getUsers({ page: event.selected + 1 ,itemsPerPage: 100 }));
    }
  };

  const isNotAdmin = user => !isGranted(roles.ADMIN, user.roles);

  const handleDeleteClick = (id) => {
    const currentPage = extractCurrentPage(pagination['@id']);
    const isConfirmed = window.confirm(
      'Voulez-vous vraiment supprimer cet utilisateur de façon définitive ?'
    );

    if (isConfirmed) {
      dispatch(deleteUser({ id })).then(() => {
        dispatch(getUsers({ page: currentPage, 'fullname': search, itemsPerPage: 100 }));
      });
    }
  };

  const extractCurrentPage = (url) => {
    const regex = /page=(\d+)/;
    const match = url.match(regex);

    return match ? parseInt(match[1]) : 1;
  }

  const usersRows = users.map(user => {
    const { id, username, firstName, lastName, office, isApproved, agency } = user;

    return (
      <tr key={id}>
        <td>
          {firstName} {lastName}
        </td>
        <td>{username}</td>
        <td>{office.location}</td>
        {isUrlContains('manage') && (
          <td>
            {isNotAdmin(user) && authUser.id !== user.id && (
              <ToggleSwitch isApproved={isApproved} userId={id} />
            )}
          </td>
        )}
        {isUrlContains('manage') && (
          <td>
            {isNotAdmin(user) && authUser.id !== user.id && (
              <ToggleAgency isAgency={agency} userId={id} />
            )}
          </td>
        )}
        <td>
          {isUrlContains('manage') &&
            (isNotAdmin(user) || user.id === authUser.id) && (
              <>
                <Link
                  state={{ prevRoute: location.pathname }}
                  to={`/users/${id}/edit`}
                  className="users-table__button"
                >
                  <i className="fa-solid fa-pencil"></i>
                </Link>
                <button
                  className="users-table__button"
                  onClick={() => handleDeleteClick(id)}
                >
                  <i className="fa-solid fa-trash"></i>
                </button>
              </>
            )}
          <Link to={`/users/${id}`} className="users-table__button">
           <i className="fa-solid fa-eye"></i>
          </Link>
        </td>
      </tr>
    );
  });

  const className = classnames('users-table', {
    'users-table--is-loading-next-page': isLoadingNextPage,
  });

  return (
    <Container>
      <div className={className}>
        <div className={'search-container'}>
          <input type={'text'} placeholder={'recherche ...'} value={search} onChange={(e) => setSearch(e.target.value)} />
          <Link to={`/users/new`} className="header__book-button new_user">
            <i className="fa fa-plus fa-1x" aria-hidden="true"></i>
          </Link>
        </div>
        <table>
          <thead>
            <tr>
              <th>Nom Prénom</th>
              <th>Email</th>
              <th>Bureau</th>
              {isUrlContains('manage') && <th>Statut</th>}
              {isUrlContains('manage') && <th>Ext/Int</th>}
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>{usersRows}</tbody>
        </table>
        <Spinner loading={isLoadingNextPage} overlay/>
      </div>
      <Paginate pagination={pagination} handlePageClick={handlePageClick}/>
    </Container>
  );
};

export default UsersList;
